import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FaProjectDiagram, 
  FaMoneyBillWave, 
  FaChartLine, 
  FaUsers, 
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaArrowUp,
  FaArrowDown,
  FaFileInvoiceDollar,
  FaTools,
  FaHandshake,
  FaTruck,
  FaBuilding,
  FaDownload,
  FaEye,
  FaEdit
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import { formatDateTime } from '../../utils/dateUtils';

// Styled Components
const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const WelcomeSection = styled.div`
  background: linear-gradient(135deg, #141340 0%, #2a2868 100%);
  border-radius: 16px;
  padding: 2rem;
  color: white;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(246, 139, 33, 0.2) 0%, transparent 70%);
    transform: translate(30%, -30%);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(246, 139, 33, 0.1) 0%, transparent 70%);
    transform: translate(-30%, 30%);
  }
`;

const WelcomeContent = styled.div`
  position: relative;
  z-index: 2;
  
  h1 {
    font-size: 2rem;
    margin-bottom: 0.5rem;
    
    span {
      color: #F68B21;
    }
  }
  
  p {
    font-size: 1.1rem;
    opacity: 0.9;
    max-width: 600px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div<{ accentColor: string }>`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(20, 19, 64, 0.05);
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(20, 19, 64, 0.1);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 6px;
    height: 100%;
    background: ${props => props.accentColor};
  }
`;

const StatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  .icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f7fa;
    color: #141340;
    font-size: 1.4rem;
  }
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #141340;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #6c757d;
`;

const StatFooter = styled.div`
  margin-top: auto;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  
  .positive {
    color: #28a745;
  }
  
  .negative {
    color: #dc3545;
  }
  
  .neutral {
    color: #6c757d;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  color: #141340;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  .icon {
    color: #F68B21;
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(450px, 1fr));
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(20, 19, 64, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(20, 19, 64, 0.1);
  }
`;

const ProjectHeader = styled.div<{ status: string }>`
  background: ${props => {
    switch (props.status) {
      case 'In Progress': return 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)';
      case 'Completed': return 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)';
      case 'Draft': return 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)';
      default: return 'linear-gradient(135deg, #141340 0%, #2a2868 100%)';
    }
  }};
  padding: 1.5rem;
  color: white;
  position: relative;
  
  h3 {
    margin: 0;
    font-size: 1.2rem;
    margin-bottom: 0.5rem;
  }
  
  .project-id {
    font-size: 0.85rem;
    opacity: 0.9;
  }
  
  .status-badge {
    position: absolute;
    top: 1.5rem;
    right: 1.5rem;
    background: rgba(255, 255, 255, 0.2);
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
  }
`;

const ProjectBody = styled.div`
  padding: 1.5rem;
`;

const ProjectInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  .info-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    color: #6c757d;
    
    .label {
      color: #141340;
      font-weight: 500;
    }
    
    .icon {
      color: #F68B21;
      font-size: 0.9rem;
    }
  }
`;

const ProjectProgress = styled.div`
  margin-bottom: 1.5rem;
  
  .progress-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
    
    .label {
      font-weight: 500;
      color: #141340;
    }
    
    .value {
      color: #F68B21;
      font-weight: 600;
    }
  }
  
  .progress-bar {
    height: 8px;
    background: #f1f1f1;
    border-radius: 4px;
    overflow: hidden;
    
    .fill {
      height: 100%;
      background: linear-gradient(90deg, #F68B21 0%, #e67c1a 100%);
      border-radius: 4px;
      transition: width 0.3s ease;
    }
  }
`;

const ProjectActions = styled.div`
  display: flex;
  gap: 1rem;
  
  a {
    flex: 1;
    padding: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    background: #f5f7fa;
    color: #141340;
    border-radius: 8px;
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: 500;
    transition: all 0.2s ease;
    
    &:hover {
      background: rgba(246, 139, 33, 0.1);
      color: #F68B21;
    }
    
    .icon {
      font-size: 0.9rem;
    }
  }
`;

const RequestsSection = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(20, 19, 64, 0.05);
`;

const RequestsHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #f1f1f1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h3 {
    margin: 0;
    font-size: 1.2rem;
    color: #141340;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    
    .icon {
      color: #F68B21;
    }
  }
  
  .view-all {
    color: #F68B21;
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const RequestsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th, td {
    padding: 1rem 1.5rem;
    text-align: left;
  }
  
  th {
    font-size: 0.9rem;
    font-weight: 600;
    color: #6c757d;
    background: #f9f9f9;
  }
  
  td {
    font-size: 0.9rem;
    border-top: 1px solid #f1f1f1;
  }
  
  tr:hover td {
    background: rgba(246, 139, 33, 0.02);
  }
  
  .request-id {
    font-weight: 500;
    color: #141340;
  }
  
  .request-project {
    color: #6c757d;
    max-width: 200px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .request-amount {
    font-weight: 600;
    color: #F68B21;
  }
  
  .request-date {
    color: #6c757d;
  }
  
  .request-status {
    display: inline-block;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    
    &.pending {
      background: rgba(255, 193, 7, 0.1);
      color: #ffc107;
    }
    
    &.approved {
      background: rgba(40, 167, 69, 0.1);
      color: #28a745;
    }
    
    &.rejected {
      background: rgba(220, 53, 69, 0.1);
      color: #dc3545;
    }
    
    &.paid {
      background: rgba(23, 162, 184, 0.1);
      color: #17a2b8;
    }
  }
  
  .request-actions {
    display: flex;
    gap: 0.5rem;
    
    button {
      background: none;
      border: none;
      color: #6c757d;
      cursor: pointer;
      padding: 0.25rem;
      border-radius: 4px;
      transition: all 0.2s ease;
      
      &:hover {
        color: #F68B21;
        background: rgba(246, 139, 33, 0.1);
      }
    }
  }
  
  @media (max-width: 1024px) {
    .request-date {
      display: none;
    }
  }
  
  @media (max-width: 768px) {
    display: block;
    overflow-x: auto;
  }
`;

const ChartSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(20, 19, 64, 0.05);
  
  .chart-header {
    padding: 1.5rem;
    border-bottom: 1px solid #f1f1f1;
    
    h3 {
      margin: 0;
      font-size: 1.2rem;
      color: #141340;
      display: flex;
      align-items: center;
      gap: 0.75rem;
      
      .icon {
        color: #F68B21;
      }
    }
  }
  
  .chart-body {
    padding: 1.5rem;
    height: 300px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6c757d;
    font-style: italic;
  }
`;



const ManagerDashboard: React.FC = () => {
  const { user } = useAuth();
  const [assignedProjects, setAssignedProjects] = useState<any[]>([]);
  const [fundRequests, setFundRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchManagerData = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        // Get assigned projects for the current manager (backend now handles filtering)
        const assignedProjectsResponse = await apiService.getAssignedProjects();
        const managerProjects = assignedProjectsResponse.data || [];
        
        console.log('Manager projects:', managerProjects);
        setAssignedProjects(managerProjects);
        
        // Get fund requests for manager's projects
        const managerProjectIds = managerProjects.map((project: any) => project._id || project.id);
        
        if (managerProjectIds.length > 0) {
          const fundRequestsResponse = await apiService.getFundRequests();
          const allRequests = fundRequestsResponse.data || [];
          
          // Filter fund requests for manager's projects
          const managerRequests = allRequests.filter((request: any) => 
            managerProjectIds.includes(request.projectId)
          );
          
          console.log('Manager fund requests:', managerRequests);
          setFundRequests(managerRequests);
        } else {
          setFundRequests([]);
        }
        
      } catch (error) {
        console.error('Error fetching manager data:', error);
        setAssignedProjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchManagerData();
  }, [user]);
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  const formatCurrency = (amount: number, currency: string = 'TZS') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  
  const getStatusClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending': return 'pending';
      case 'approved': return 'approved';
      case 'rejected': return 'rejected';
      case 'paid': return 'paid';
      default: return '';
    }
  };
  
  const totalProjects = assignedProjects.length;
  const activeProjects = assignedProjects.filter(p => p.status === 'In Progress').length;
  const completedProjects = assignedProjects.filter(p => p.status === 'Completed').length;
  const draftProjects = assignedProjects.filter(p => p.status === 'Draft').length;
  
  // Count fund requests that are specifically pending Project Manager approval
  const pendingRequests = fundRequests.filter(r => {
    // Check if there's a pending approval step for Project Manager
    const pendingManagerStep = r.approvalChain?.find((step: any) => 
      step.role === 'Project Manager' && step.status === 'Pending'
    );
    return pendingManagerStep !== undefined;
  }).length;
  
  return (
    <DashboardContainer>
      <WelcomeSection>
        <WelcomeContent>
          <h1>Welcome back, <span>{user?.name}</span></h1>
          <p>
            Here's an overview of your projects and fund requests. You have {pendingRequests} pending fund requests that require your attention.
          </p>
        </WelcomeContent>
      </WelcomeSection>
      
      <StatsGrid>
        <StatCard accentColor="#3498db">
          <StatHeader>
            <div className="icon">
              <IconWrapper icon={FaProjectDiagram} />
            </div>
          </StatHeader>
          <StatValue>{totalProjects}</StatValue>
          <StatLabel>Total Projects</StatLabel>
          <StatFooter>
            <span className="neutral">Assigned to you</span>
          </StatFooter>
        </StatCard>
        
        <StatCard accentColor="#2ecc71">
          <StatHeader>
            <div className="icon">
              <IconWrapper icon={FaCheckCircle} />
            </div>
          </StatHeader>
          <StatValue>{activeProjects}</StatValue>
          <StatLabel>Active Projects</StatLabel>
          <StatFooter>
            <IconWrapper icon={FaArrowUp} className="positive" />
            <span className="positive">2 more than last month</span>
          </StatFooter>
        </StatCard>
        
        <StatCard accentColor="#f39c12">
          <StatHeader>
            <div className="icon">
              <IconWrapper icon={FaMoneyBillWave} />
            </div>
          </StatHeader>
          <StatValue>{pendingRequests}</StatValue>
          <StatLabel>Pending Requests</StatLabel>
          <StatFooter>
            <IconWrapper icon={FaClock} className="neutral" />
            <span className="neutral">Awaiting your approval</span>
          </StatFooter>
        </StatCard>
        
        <StatCard accentColor="#9b59b6">
          <StatHeader>
            <div className="icon">
              <IconWrapper icon={FaChartLine} />
            </div>
          </StatHeader>
          <StatValue>{formatCurrency(45000000)}</StatValue>
          <StatLabel>Total Budget</StatLabel>
          <StatFooter>
            <IconWrapper icon={FaArrowDown} className="negative" />
            <span className="negative">12% spent this month</span>
          </StatFooter>
        </StatCard>
      </StatsGrid>
      
      <SectionTitle>
        <IconWrapper icon={FaProjectDiagram} className="icon" />
        Your Projects
      </SectionTitle>
      
      <ProjectsGrid>
        {assignedProjects.slice(0, 4).map(project => (
          <ProjectCard key={project.id || project._id}>
            <ProjectHeader status={project.status}>
              <h3>{project.name}</h3>
              <div className="project-id">{project.id || project._id}</div>
              <div className="status-badge">{project.status}</div>
            </ProjectHeader>
            <ProjectBody>
              <ProjectInfo>
                <div className="info-item">
                  <IconWrapper icon={FaMapMarkerAlt} className="icon" />
                  <span className="label">Location:</span> {project.location}
                </div>
                <div className="info-item">
                  <IconWrapper icon={FaCalendarAlt} className="icon" />
                  <span className="label">Deadline:</span> {formatDate(project.endDate || new Date().toISOString())}
                </div>
                <div className="info-item">
                  <IconWrapper icon={FaUsers} className="icon" />
                  <span className="label">Team:</span> {project.assignedStaff?.length || 0} members
                </div>
                <div className="info-item">
                  <IconWrapper icon={FaMoneyBillWave} className="icon" />
                  <span className="label">Budget:</span> {formatCurrency(project.budget.total)}
                </div>
              </ProjectInfo>
              
              <ProjectProgress>
                <div className="progress-header">
                  <div className="label">Progress</div>
                  <div className="value">{project.progress || 0}%</div>
                </div>
                <div className="progress-bar">
                  <div className="fill" style={{ width: `${project.progress || 0}%` }}></div>
                </div>
              </ProjectProgress>
              
              <ProjectActions>
                <Link to={`/manager/projects/${project.id || project._id}`}>
                  <IconWrapper icon={FaEye} className="icon" />
                  View Details
                </Link>
                <Link to={`/manager/projects/${project.id || project._id}/edit`}>
                  <IconWrapper icon={FaEdit} className="icon" />
                  Manage
                </Link>
              </ProjectActions>
            </ProjectBody>
          </ProjectCard>
        ))}
      </ProjectsGrid>
      
      <RequestsSection>
        <RequestsHeader>
          <h3>
            <IconWrapper icon={FaFileInvoiceDollar} className="icon" />
            Recent Fund Requests
          </h3>
          <Link to="/manager/fund-requests" className="view-all">View All</Link>
        </RequestsHeader>
        
        <div style={{ overflowX: 'auto' }}>
          <RequestsTable>
            <thead>
              <tr>
                <th>Request ID</th>
                <th>Project</th>
                <th>Requester</th>
                <th>Amount</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {fundRequests
                .sort((a, b) => {
                  // Prioritize requests pending Project Manager approval
                  const aPendingForManager = a.approvalChain?.find((step: any) => 
                    step.role === 'Project Manager' && step.status === 'Pending'
                  );
                  const bPendingForManager = b.approvalChain?.find((step: any) => 
                    step.role === 'Project Manager' && step.status === 'Pending'
                  );
                  
                  if (aPendingForManager && !bPendingForManager) return -1;
                  if (!aPendingForManager && bPendingForManager) return 1;
                  
                  // Then sort by creation date (newest first)
                  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
                })
                .slice(0, 5)
                .map(request => (
                <tr key={request.id || request._id}>
                  <td className="request-id">{request.id || request._id}</td>
                  <td className="request-project">{request.projectName}</td>
                  <td>{request.requesterId?.name || request.requester}</td>
                  <td className="request-amount">{formatCurrency(request.amount)}</td>
                  <td className="request-date">{formatDateTime(request.createdAt)}</td>
                  <td>
                    <span className={`request-status ${getStatusClass(request.status.toLowerCase())}`}>
                      {request.status}
                    </span>
                  </td>
                  <td className="request-actions">
                    <Link to={`/manager/fund-requests/${request.id || request._id}`} style={{ color: 'inherit' }}>
                      <button title="View Details">
                        <IconWrapper icon={FaEye} />
                      </button>
                    </Link>
                    {request.approvalChain?.find((step: any) => 
                      step.role === 'Project Manager' && step.status === 'Pending'
                    ) && (
                      <Link to={`/manager/fund-requests/${request.id || request._id}`} style={{ color: 'inherit' }}>
                        <button title="Review & Approve">
                          <IconWrapper icon={FaCheckCircle} />
                        </button>
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
              {fundRequests.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                    No fund requests found for your projects
                  </td>
                </tr>
              )}
            </tbody>
          </RequestsTable>
        </div>
      </RequestsSection>
      
      <ChartSection>
        <ChartCard>
          <div className="chart-header">
            <h3>
              <IconWrapper icon={FaChartLine} className="icon" />
              Budget Utilization
            </h3>
          </div>
          <div className="chart-body">
            Chart visualization will be implemented here
          </div>
        </ChartCard>
        
        <ChartCard>
          <div className="chart-header">
            <h3>
              <IconWrapper icon={FaMoneyBillWave} className="icon" />
              Expense Categories
            </h3>
          </div>
          <div className="chart-body">
            Chart visualization will be implemented here
          </div>
        </ChartCard>
      </ChartSection>
    </DashboardContainer>
  );
};

export default ManagerDashboard; 